<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    percent?: number
    size?: number
    stroke?: number
    altIcon?: boolean
    subchargeDisabled?: boolean
    chargingNow?: boolean
  }>(),
  {
    size: 48,
    stroke: 5
  }
)

const circumference = computed(() => 2 * Math.PI * (props.size / 2 - props.stroke))
const offset = computed(() => {
  const fraction = (props.percent || 0) / 100
  return circumference.value * (1 - fraction)
})
</script>

<template>
  <div
    class="student-mainpage-subcharge-circle"
    :class="{ 'student-mainpage-subcharge-circle_pulse': chargingNow }"
    :style="{ width: `${size}px`, height: `${size}px` }"
    role="progressbar"
    :aria-valuenow="percent"
  >
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <!-- background -->
      <circle
        :cx="size / 2"
        :cy="size / 2"
        :r="size / 2 - stroke"
        fill="none"
        stroke="#E8E8E8"
        :stroke-width="stroke"
      />
      <!-- track -->
      <circle
        :cx="size / 2"
        :cy="size / 2"
        :r="size / 2 - stroke"
        fill="none"
        stroke="#53BB01"
        :stroke-width="stroke"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="offset"
        stroke-linecap="round"
        :transform="`rotate(-90 ${size / 2} ${size / 2})`"
        style="transition: all 0.3s"
      />

      <!-- cross when subcharge is disabled -->
      <path
        v-if="subchargeDisabled"
        :d="`M6 6 ${size - 6} ${size - 6}M6 ${size - 6} ${size - 6} 6`"
        stroke="black"
        :stroke-width="stroke"
        stroke-linecap="round"
      />
    </svg>

    <v-icon v-if="altIcon" key="1" :size="size / 2" class="student-mainpage-subcharge-circle__blink">
      mdi-battery-off-outline
    </v-icon>
    <v-icon v-else key="0" :size="size / 2"> mdi-lightning-bolt </v-icon>
  </div>
</template>

<style lang="scss">
.student-mainpage-subcharge-circle {
  border-radius: 999px;
  background: white;
  position: relative;

  .v-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__blink {
    animation: blink 1.5s infinite;
  }

  &_pulse {
    &::after,
    &::before {
      content: '';
      position: absolute;
      border: 3px solid #53bb01;
      left: -20px;
      opacity: 0;
      right: -20px;
      top: -20px;
      bottom: -20px;
      border-radius: 50%;
      animation: pulse 2.5s linear infinite;
    }

    &::after {
      animation-delay: 1.25s;
    }
  }
}

@keyframes blink {
  0%,
  74% {
    opacity: 1;
  }
  75%,
  100% {
    opacity: 0;
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}
</style>
